import React, { useEffect, useContext, useState, useRef } from 'react';
import ReactGA from 'react-ga4';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import CardTabs from './components/CardTabs';
import Videocorousel from './components/Videocorousel';
import PartnerBanks from './components/PartnerBanks';
import NewsOffers from './components/NewsOffers';
import Consultation from './components/Consultation';
import Footer from './components/Footer';
import SmartAssistant from './components/SmartAssistant';
import Calculator from './components/Calculator';
import BankDetails from './components/BankDetails';
import CardDetailsPage from './components/CardBlog';
import ScrollToTop from './common/ScrollToTop';
import AboutUs from './components/AboutUs';
import Contact from './components/Contact';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AdvertiserDisclosure from './components/AdvertiserDisclosure';
import ConsultationPage from './components/ConsultationPage';
import OfferAndNewsDetail from './components/OfferAndNewsDetail';
import LoginModal from './components/LoginModal';

import { AuthProvider, AuthContext } from './context/AuthContext';
import LoginPage from './pages/LoginPage';

// Google Analytics
const TRACKING_ID = 'G-0D6NSJL4L2';
ReactGA.initialize(TRACKING_ID);

function HomePage() {
  const { user } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const hasShown = useRef(false);

  useEffect(() => {
    // Don't show if already logged in or already shown this session
    if (user) return;
    if (sessionStorage.getItem('loginModalShown')) return;

    const handleScroll = () => {
      if (hasShown.current) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      if (scrollPercent >= 70) {
        hasShown.current = true;
        setShowModal(true);
        sessionStorage.setItem('loginModalShown', 'true');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [user]);

  const handleClose = () => setShowModal(false);

  return (
    <>
      <HeroSection />
      <section className="card-section">
        <CardTabs />
      </section>
      <PartnerBanks />
      <Videocorousel />
      <NewsOffers />
      <Consultation />

      {showModal && <LoginModal onClose={handleClose} />}
    </>
  );
}

function LayoutWrapper() {
  const location = useLocation();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
    });
  }, [location]);

  return (
    <>
      <div>
        <Navbar />
        <ScrollToTop />

        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/partner-banks" element={<PartnerBanks />} />
            <Route path="/partner-banks/:bankSlug" element={<BankDetails />} />
            <Route path="/credit-cards" element={<CardTabs />} />
            <Route path="/credit-cards/:slug" element={<CardDetailsPage />} />
            <Route path="/offers-news" element={<NewsOffers />} />
            <Route path="/calculators" element={<Calculator />} />
            <Route path="/smart-assistant" element={<SmartAssistant />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route
              path="/offer-and-news/:id"
              element={<OfferAndNewsDetail />}
            />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route
              path="/advertiser-disclosure"
              element={<AdvertiserDisclosure />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {location.pathname !== '/login' && <Footer />}
      </div>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <LayoutWrapper />
      </Router>
    </AuthProvider>
  );
}
